/**
 * Notification Manager Service
 * Handles real-time notifications using Laravel Echo and Pusher
 */
import notificationsApi from '@/api/notifications';
import pusherService from '@/services/pusher';

class NotificationManager {
  constructor() {
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners = {
      'onNotificationReceived': [],
      'onNotificationUpdated': [],
      'onNotificationDeleted': [],
      'onCountUpdated': [],
      'onPaginationStateChanged': []
    };
    this.initialized = false;
    this.userId = null;
    this.echo = null;
    
    // Pagination state
    this.currentPage = 1;
    this.lastPage = 1;
    this.perPage = 10;
    this.totalNotifications = 0;
    this.hasMore = false;
    this.isLoadingMore = false;
    this.currentFilter = 'all'; // 'all' or 'unread'
    this.paginationError = null;
  }

  /**
   * Initialize the notification manager with user ID
   * @param {string|number} userId - The current user's ID
   */
  async init(userId) {
    // If already initialized for the same user, return
    if (this.initialized && this.userId === userId) return;
    
    // If initialized for different user, cleanup first
    if (this.initialized && this.userId !== userId) {
      await this.cleanup();
    }
    
    this.userId = userId;
    
    try {
      // Initialize Echo if window is available (to avoid SSR issues)
      if (typeof window !== 'undefined') {
        await this.initializeEcho();
        this.setupListeners();
      }
      
      // Fetch initial notifications
      await this.fetchNotifications();
      
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing notification manager:', error);
      throw error;
    }
  }
  
  /**
   * Initialize Laravel Echo with Pusher using centralized service
   */
  async initializeEcho() {
    try {
      // Use centralized pusher service
      this.echo = await pusherService.connect(this.userId);
    } catch (error) {
      console.error('Error initializing Echo via pusher service:', error);
      throw error;
    }
  }
  
  /**
   * Setup Echo listeners for real-time updates
   */
  setupListeners() {
    if (!this.echo) return;
    
    // Subscribe to the private user channel using pusher service
    const channel = pusherService.privateChannel(`user.${this.userId}`);
    
    if (!channel) {
      console.warn('Failed to setup private channel for notifications');
      return;
    }

    // Listen for new notifications
    channel.listen('.notification.created', (data) => {
      this.handleNewNotification(data);
    });

    // Listen for notification updates (e.g., read status)
    channel.listen('.notification.updated', (data) => {
      this.handleNotificationUpdate(data);
    });

    // Listen for notification deletions
    channel.listen('.notification.deleted', (data) => {
      this.handleNotificationDeletion(data);
    });

    // Listen for unread count updates
    channel.listen('.notification.count', (data) => {
      this.handleCountUpdate(data);
    });
  }
  
  /**
   * Fetch initial notifications from the server
   * @param {boolean} reset - Whether to reset pagination state
   */
  async fetchNotifications(reset = true) {
    try {
      if (reset) {
        this.resetPagination();
      }
      
      const params = {
        page: this.currentPage,
        per_page: this.perPage
      };
      
      // Add unread filter if needed
      if (this.currentFilter === 'unread') {
        params.unread = true;
      }
      
      // Using the notifications API module with pagination
      const response = await notificationsApi.getAll(params);
      
      // Transform API response to the format expected by the UI
      if (response.data?.data && Array.isArray(response.data.data)) {
        // Filter out any null or undefined notifications before transforming
        const validNotifications = response.data.data.filter(notification => notification != null);
        
        if (validNotifications.length > 0) {
          const transformedNotifications = validNotifications.map(notification => this.transformNotification(notification));
          
          if (reset) {
            this.notifications = transformedNotifications;
          } else {
            // Append new notifications, avoiding duplicates
            const existingIds = new Set(this.notifications.map(n => n.id));
            const newNotifications = transformedNotifications.filter(n => !existingIds.has(n.id));
            this.notifications = [...this.notifications, ...newNotifications];
          }
        } else if (reset) {
          this.notifications = [];
        }
      } else if (reset) {
        this.notifications = [];
      }
      
      // Update pagination state from meta
      if (response.data?.meta) {
        this.currentPage = response.data.meta.current_page;
        this.lastPage = response.data.meta.last_page;
        this.perPage = response.data.meta.per_page;
        this.totalNotifications = response.data.meta.total;
        this.hasMore = this.currentPage < this.lastPage;
      }
      
      // Fetch unread count
      const countResponse = await notificationsApi.getUnreadCount();
      this.unreadCount = countResponse.data?.count || 0;
      
      // Clear pagination error
      this.paginationError = null;
      
      // Notify listeners
      this.notifyListeners('onCountUpdated', this.unreadCount);
      this.notifyListeners('onPaginationStateChanged', {
        currentPage: this.currentPage,
        lastPage: this.lastPage,
        hasMore: this.hasMore,
        totalNotifications: this.totalNotifications,
        isLoadingMore: this.isLoadingMore,
        error: this.paginationError
      });
      
      return this.notifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      this.paginationError = error.message || 'Failed to load notifications';
      
      if (reset) {
        this.notifications = [];
      }
      
      // Notify listeners of error
      this.notifyListeners('onPaginationStateChanged', {
        currentPage: this.currentPage,
        lastPage: this.lastPage,
        hasMore: this.hasMore,
        totalNotifications: this.totalNotifications,
        isLoadingMore: false,
        error: this.paginationError
      });
      
      return [];
    }
  }
  
  /**
   * Load more notifications for infinite scroll
   */
  async loadMoreNotifications() {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }
    
    this.isLoadingMore = true;
    this.paginationError = null;
    
    // Notify listeners of loading state
    this.notifyListeners('onPaginationStateChanged', {
      currentPage: this.currentPage,
      lastPage: this.lastPage,
      hasMore: this.hasMore,
      totalNotifications: this.totalNotifications,
      isLoadingMore: this.isLoadingMore,
      error: this.paginationError
    });
    
    try {
      const nextPage = this.currentPage + 1;
      const params = {
        page: nextPage,
        per_page: this.perPage
      };
      
      // Add unread filter if needed
      if (this.currentFilter === 'unread') {
        params.unread = true;
      }
      
      const response = await notificationsApi.getAll(params);
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        const validNotifications = response.data.data.filter(notification => notification != null);
        
        if (validNotifications.length > 0) {
          const transformedNotifications = validNotifications.map(notification => this.transformNotification(notification));
          
          // Append new notifications, avoiding duplicates
          const existingIds = new Set(this.notifications.map(n => n.id));
          const newNotifications = transformedNotifications.filter(n => !existingIds.has(n.id));
          this.notifications = [...this.notifications, ...newNotifications];
        }
      }
      
      // Update pagination state
      if (response.data?.meta) {
        this.currentPage = response.data.meta.current_page;
        this.lastPage = response.data.meta.last_page;
        this.hasMore = this.currentPage < this.lastPage;
      }
      
      this.isLoadingMore = false;
      
      // Notify listeners
      this.notifyListeners('onPaginationStateChanged', {
        currentPage: this.currentPage,
        lastPage: this.lastPage,
        hasMore: this.hasMore,
        totalNotifications: this.totalNotifications,
        isLoadingMore: this.isLoadingMore,
        error: this.paginationError
      });
      
      return this.notifications;
    } catch (error) {
      console.error('Failed to load more notifications:', error);
      this.isLoadingMore = false;
      this.paginationError = error.message || 'Failed to load more notifications';
      
      // Notify listeners of error
      this.notifyListeners('onPaginationStateChanged', {
        currentPage: this.currentPage,
        lastPage: this.lastPage,
        hasMore: this.hasMore,
        totalNotifications: this.totalNotifications,
        isLoadingMore: this.isLoadingMore,
        error: this.paginationError
      });
      
      return this.notifications;
    }
  }
  
  /**
   * Reset pagination state
   */
  resetPagination() {
    this.currentPage = 1;
    this.lastPage = 1;
    this.hasMore = false;
    this.isLoadingMore = false;
    this.paginationError = null;
  }
  
  /**
   * Set the current filter and reset pagination
   * @param {string} filter - 'all' or 'unread'
   */
  async setFilter(filter) {
    if (this.currentFilter !== filter) {
      this.currentFilter = filter;
      await this.fetchNotifications(true);
    }
  }
  
  /**
   * Transform API notification to UI format
   * @param {Object} notification - The notification from API
   * @returns {Object} - Transformed notification
   */
  transformNotification(notification) {
    
    // If notification is null or undefined, return a default object
    if (!notification) {
      console.warn('Received null or undefined notification');
      return null;
    }
    
    // Map severity_type or type to UI type (success, info, danger, warning)
    const typeMap = {
      'success': 'success',
      'info': 'info',
      'error': 'danger',
      'danger': 'danger',
      'warning': 'warning'
    };
    
    // Check for existing notification to prevent duplicates
    const existingIndex = this.notifications.findIndex(n => n.id === notification.id);
    if (existingIndex !== -1) {
      return {
        ...this.notifications[existingIndex],
        ...notification
      };
    }
    
    const transformedNotification = {
      id: notification.id || Date.now(),
      // Use type if provided directly, otherwise check severity_type
      type: typeMap[notification.type] || typeMap[notification.severity_type] || 'info',
      title: notification.title || 'Notification',
      message: notification.message || 'No details available',
      time: notification.created_at || new Date().toISOString(),
      // Use direct unread property if provided, otherwise check read_at
      unread: typeof notification.unread === 'boolean' ? notification.unread : notification.read_at === null,
      notification_type: notification.notification_type || 'system',
      data: notification.data || {},
      created_at: notification.created_at || new Date().toISOString(),
      updated_at: notification.updated_at || new Date().toISOString(),
      read_at: notification.read_at
    };
    
    return transformedNotification;
  }
  
  /**
   * Handle a new notification
   * @param {Object} notification - The new notification
   */
  handleNewNotification(notification) {
    // Check if notification already exists to prevent duplicates
    const existingIndex = this.notifications.findIndex(n => n.id === notification.id);
    if (existingIndex !== -1) {
      console.log(`Notification with ID ${notification.id} already exists, skipping`);
      return;
    }
    
    // Transform the notification from API format to UI format
    const transformedNotification = this.transformNotification(notification);
    
    // Add to our local collection at the top
    this.notifications.unshift(transformedNotification);
    
    // Update total count for pagination
    this.totalNotifications++;
    
    // Update unread count if it's unread
    if (transformedNotification.unread) {
      this.unreadCount++;
      this.notifyListeners('onCountUpdated', this.unreadCount);
    }
    
    // Notify listeners
    this.notifyListeners('onNotificationReceived', transformedNotification);
    
    // Notify pagination state change (total count updated)
    this.notifyListeners('onPaginationStateChanged', {
      currentPage: this.currentPage,
      lastPage: this.lastPage,
      hasMore: this.hasMore,
      totalNotifications: this.totalNotifications,
      isLoadingMore: this.isLoadingMore,
      error: this.paginationError
    });
  }
  
  /**
   * Handle notification update (e.g. read status)
   * @param {Object} data - The updated notification data
   */
  handleNotificationUpdate(data) {
    const index = this.notifications.findIndex(n => n.id === data.id);
    if (index !== -1) {
      // Check if read status changed
      const wasUnread = this.notifications[index].unread;
      const isNowUnread = data.unread;
      
      // Update the notification
      this.notifications[index] = {
        ...this.notifications[index],
        ...data
      };
      
      // Update unread count if status changed
      if (wasUnread && !isNowUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.notifyListeners('onCountUpdated', this.unreadCount);
      } else if (!wasUnread && isNowUnread) {
        this.unreadCount++;
        this.notifyListeners('onCountUpdated', this.unreadCount);
      }
      
      // Notify listeners
      this.notifyListeners('onNotificationUpdated', this.notifications[index]);
    }
  }
  
  /**
   * Handle notification deletion
   * @param {Object} data - The deleted notification data
   */
  handleNotificationDeletion(data) {
    const index = this.notifications.findIndex(n => n.id === data.id);
    if (index !== -1) {
      // Check if it was unread before removing
      const wasUnread = this.notifications[index].unread;
      
      // Remove from our collection
      const deleted = this.notifications.splice(index, 1)[0];
      
      // Update unread count if it was unread
      if (wasUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.notifyListeners('onCountUpdated', this.unreadCount);
      }
      
      // Notify listeners
      this.notifyListeners('onNotificationDeleted', deleted);
    }
  }
  
  /**
   * Handle unread count update
   * @param {Object} data - The count data
   */
  handleCountUpdate(data) {
    this.unreadCount = data.count;
    
    // Notify listeners
    this.notifyListeners('onCountUpdated', this.unreadCount);
  }
  
  /**
   * Mark a notification as read
   * @param {string|number} notificationId - The notification ID
   */
  async markAsRead(notificationId) {
    try {
      // Using the notifications API module
      await notificationsApi.markAsRead(notificationId);
      // The server will broadcast the update, which will be handled by our listeners
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }
  
  /**
   * Mark a notification as unread
   * @param {string|number} notificationId - The notification ID
   */
  async markAsUnread(notificationId) {
    try {
      // Using the notifications API module
      await notificationsApi.markAsUnread(notificationId);
      // The server will broadcast the update, which will be handled by our listeners
      return true;
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
      return false;
    }
  }
  
  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      // Using the notifications API module
      await notificationsApi.markAllAsRead();
      // The server will broadcast the updates, which will be handled by our listeners
      return true;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return false;
    }
  }
  
  /**
   * Delete a notification
   * @param {string|number} notificationId - The notification ID
   */
  async deleteNotification(notificationId) {
    try {
      // Using the notifications API module
      await notificationsApi.deleteNotification(notificationId);
      // The server will broadcast the deletion, which will be handled by our listeners
      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  }
  
  /**
   * Add an event listener
   * @param {string} event - The event name
   * @param {Function} callback - The callback function
   */
  addEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }
  
  /**
   * Remove an event listener
   * @param {string} event - The event name
   * @param {Function} callback - The callback function
   */
  removeEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }
  
  /**
   * Cleanup all resources and reset state
   */
  async cleanup() {
    console.log('Cleaning up notification manager...');
    
    try {
      // Remove all event listeners
      this.removeAllEventListeners();
      
      // Reset state
      this.reset();
      
      // Note: We don't disconnect pusher here as it's managed centrally
      // The pusher service handles connection cleanup
      this.echo = null;
      
      console.log('Notification manager cleanup completed');
    } catch (error) {
      console.error('Error during notification manager cleanup:', error);
    }
  }

  /**
   * Reset notification state
   */
  reset() {
    this.notifications = [];
    this.unreadCount = 0;
    this.initialized = false;
    this.userId = null;
    
    // Reset pagination state
    this.resetPagination();
    
    console.log('Notification manager state reset');
  }

  /**
   * Remove all event listeners
   */
  removeAllEventListeners() {
    for (const event in this.listeners) {
      this.listeners[event] = [];
    }
  }
  
  /**
   * Notify all listeners of an event
   * @param {string} event - The event name
   * @param {*} data - The event data
   */
  notifyListeners(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
}

// Export a singleton instance
export const notificationManager = new NotificationManager();

export default notificationManager; 